import React, {createElement} from 'react';
import Icon, {Sizes} from '@pb/kit-component-icon';
import Loading, {LoadingHolder, Types as LoadingTypes} from '@pb/kit-component-loading';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import type {ComponentProps} from 'react';
import type {IconSize, IconType, IconProps} from '@pb/kit-component-icon';

import styles from './icon-button.component.module.styl';
import {FlatButton} from '@pb/kit-component-button';


const noop = () => undefined;
const classNames = withCssModule(styles);
const classes = hashedClasses(styles);


type NativeButtonProps = Omit<ComponentProps<'button'>, 'ref'>;

export interface BasicIconButtonProps<AS extends 'a' | 'button' = 'button'> extends NativeButtonProps {
    /**
     * Root element type. Use `"a"` together with `href` to render a link styled
     * as an icon button. Defaults to `"button"`.
     */
    as?: AS,
    /**
     * When `true`, renders the button as an inline element instead of a block,
     * allowing it to sit within flowing text.
     */
    inline?: boolean,
    /**
     * Legacy click/navigation handler. Pass a string to render the button as
     * an anchor link; pass a function to use as an `onClick` callback.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. link functionality is deprecated, use onClick instead
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handler?: string | ((name: any | undefined, event: React.MouseEvent<HTMLButtonElement>) => unknown),
    /**
     * When `true`, opens a string `handler` URL in the same tab (internal link).
     * When `false`, opens in a new tab (`target="_blank"`).
     *
     * @deprecated It will be removed in 6 months 09-15-2025. link functionality is deprecated, use onClick instead
     */
    internal?: boolean,
    /**
     * Identifier passed as the first argument to a function `handler`.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. name and handler are deprecated, use onClick instead
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    name?: any,
    /**
     * Additional CSS class names merged with the component's own classes.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use className instead
     */
    mix?: string,
    /**
     * When `true`, overlays the icon with a `Loading` spinner and sets
     * `aria-busy="true"` on the button. Use to indicate an in-progress action.
     */
    loader?: boolean,
    /**
     * URL the element navigates to when `as="a"`. Temporary prop until the
     * legacy Flow layer is removed.
     */
    href?: string,
    /**
     * Link target attribute when `as="a"` (e.g. `"_blank"`). Temporary prop
     * until the legacy Flow layer is removed.
     */
    target?: ComponentProps<'a'>['target'],
    /**
     * Icon configuration object forwarded to the inner `<Icon>` component.
     * Accepts any `IconProps` including `type`, `size`, and `className`.
     * Preferred over the deprecated `icon`/`iconSize` props.
     */
    iconProps?: IconProps,
    /**
     * Icon name from the icon collection. Pass this together with `iconSize`.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use iconProps instead
     */
    icon?: IconType,
    /**
     * Icon size token applied to the inner `<Icon>`. Defaults to `Sizes.Size_14`.
     *
     * @deprecated It will be removed in 6 months 09-15-2025. use iconProps instead
     */
    iconSize?: IconSize,
    /**
     * Forwarded ref to the underlying `<button>` or `<a>` element.
     */
    ref?: React.Ref<AS extends 'a' ? HTMLAnchorElement : HTMLButtonElement>,
}

/**
 * Low-level icon-only button that composes an `<Icon>` inside a `<button>` or
 * `<a>` element, with optional loading-spinner overlay.
 *
 * This is the base building block used by `IconButton`. Prefer `IconButton` for
 * most use cases; use `BasicIconButton` directly when you need the raw
 * foundation without size/selection styles.
 *
 * Pass all icon configuration through `iconProps` (preferred). The legacy
 * `icon`/`iconSize` props remain for backwards compatibility and are deprecated.
 *
 * When `loader` is `true` the icon is visually overlaid with a `Loading`
 * spinner and `aria-busy="true"` is set on the button, suppressing interaction
 * until loading completes.
 *
 * Renders as a `<button>` by default; set `as="a"` with an `href` to render an
 * accessible link styled as an icon button.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/components-icon-button--default-icon-buttons-template}
 */
export function BasicIconButton<AS extends 'a' | 'button' = 'button'>(props: BasicIconButtonProps<AS>) {
    const {
        as = 'button',
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        internal = true,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        handler = noop,
        loader = false,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        mix = '',
        className = '',
        inline = false,
        iconProps,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        icon,
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
        iconSize = Sizes.Size_14,
        type = 'button',
        ref,
        ...restProps
    } = props;

    const iconButtonClasses = classNames(mix, className, 'icon-button', {
        'icon-button_inline': inline,
        'icon-button_with-loader': loader,
    });

    let buttonProps = {};
    if (as === 'button') {
        const {disabled = false, onClick} = restProps;

        buttonProps = {
            type,
            disabled,
            'onClick': (e: React.MouseEvent<HTMLButtonElement>) => {
                if (!disabled && typeof handler === 'function') {
                    // eslint-disable-next-line @typescript-eslint/no-deprecated -- own deprecated API (WS8)
                    handler(props.name, e);
                }
                onClick?.(e);
            },
            ref,
            'className': iconButtonClasses,
            'aria-busy': loader ? true : undefined,
        };
    }

    const children = (
        <>
            <Icon
                size={iconSize}
                {...iconProps}
                type={iconProps?.type || icon || ''}
            />
            {loader ? (
                <LoadingHolder>
                    <Loading type={LoadingTypes.ICON} aria-hidden="true"/>
                </LoadingHolder>
            ) : null}
        </>
    );

    if (typeof handler === 'string') {
        return (
            <FlatButton
                as="a"
                href={handler}
                target={internal ? undefined : '_blank'}
                {...buttonProps}>
                {children}
            </FlatButton>
        );
    }

    return createElement(
        as,
        {
            ...restProps,
            ...buttonProps,
            className: iconButtonClasses,
        },
        as === 'button'
            ? <span className={classes['icon-button__hole']}>{children}</span>
            : children,
    );
}
